import {
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { selectSelectedHeader, updateContentHeaderById } from 'features/document/documentSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AzulDropdownMenu } from '@the-ksquare-group/azul-ds';
import { ChangeEvent, Fragment, SyntheticEvent, useRef } from 'react';
import { colorPalette, docGeniusPalette } from 'styles/partials/colors';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteFileMutation, useGetFileQuery, usePostFileMutation } from 'features/files/filesApi';
import { toBase64 } from 'utils/fileToBase64';
import { useParams } from 'react-router-dom';
import { GridContainer, GridSubcontainer, HiddenButton, Subtitle, Title } from './HeaderSidebar.styled';
import Button from 'components/Button/Button';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AlignmentRadioGroup from 'components/AlignmentRadioGroup/AlignmentRadioGroup';
import { Tag } from 'components/RadioTag/RadioTag';
import { Divider } from 'pages/DocumentBuilder/components/HeaderSection/HeaderSection.styled';

export default function HeaderSidebar() {
  const selectedHeader = useAppSelector(selectSelectedHeader);
  if (selectedHeader === null) {
    return null;
  }
  const { id } = useParams();
  const hiddenBackgroundImageInput = useRef<HTMLInputElement>(null);
  const hiddenLogoImageInput = useRef<HTMLInputElement>(null);

  const { content, style } = selectedHeader.data;
  const [postFile] = usePostFileMutation();
  const [deleteFile] = useDeleteFileMutation();
  const dispatch = useAppDispatch();

  const handleClickBackgroundImage = () => {
    hiddenBackgroundImageInput.current?.click();
  };
  const handleClickLogo = () => {
    hiddenLogoImageInput.current?.click();
  };

  const { data: backgroundImageData, isLoading: isLoadingBackgroundImage } = useGetFileQuery(
    Number(content.backgroundImage.id),
    {
      skip: content.backgroundImage.id === null,
    }
  );
  const { data: logoData, isLoading: isLoadingLogo } = useGetFileQuery(Number(content.logo.id), {
    skip: content.logo.id === null,
  });

  const styleOptions = {
    labelFont: [
      { label: 'Roboto', value: 'Roboto' },
      { label: 'Calibri', value: 'Calibri' },
      { label: 'Courier', value: 'Courier' },
    ],
    labelFontSize: [
      { label: '12px', value: '12px' },
      { label: '13px', value: '13px' },
      { label: '14px', value: '14px' },
      { label: '15px', value: '15px' },
      { label: '16px', value: '16px' },
      { label: '17px', value: '17px' },
      { label: '18px', value: '18px' },
      { label: '19px', value: '19px' },
      { label: '20px', value: '20px' },
      { label: '22px', value: '22px' },
    ],
  };

  const handleChangeColor = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateContentHeaderById({
        style: {
          backgroundColor: event.target.value,
        },
      })
    );
  };

  const handleChangePageNumberColor = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateContentHeaderById({
        style: {
          fontColor: event.target.value,
        },
      })
    );
  };

  const handleBackgroundControl = () => {
    dispatch(
      updateContentHeaderById({
        content: {
          hasBackgroundColor: !content.hasBackgroundColor,
        },
      })
    );
  };

  const handleChangeHasLogo = () => {
    dispatch(
      updateContentHeaderById({
        content: {
          hasLogo: !content.hasLogo,
        },
      })
    );
  };

  const handleChangeDetails = () => {
    dispatch(
      updateContentHeaderById({
        content: {
          hasHeaderDetails: !content.hasHeaderDetails,
        },
      })
    );
  };

  const handleUpdateByStyle = (style: string) => {
    return (event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) => {
      dispatch(
        updateContentHeaderById({
          style: {
            [style]: event.target.value,
          },
        })
      );
    };
  };

  const handleUploadBackgroundImage = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files !== null) {
      const srcName = event.currentTarget.files[0].name;
      const imageBase64 = await toBase64(event.currentTarget.files[0]);
      const imageContent = imageBase64.split(',')[1];
      const fileResult = await postFile({
        type: 'jpeg',
        fileName: srcName,
        fileContent: imageContent,
      }).unwrap();
      if (content.backgroundImage.id && fileResult && !id) {
        deleteFile(content.backgroundImage.id);
      }
      dispatch(
        updateContentHeaderById({
          content: {
            backgroundImage: {
              id: fileResult.data?.id,
            },
          },
        })
      );
    }
    event.target.value = '';
  };

  const handleUploadLogo = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files !== null) {
      const srcName = event.currentTarget.files[0].name;
      const imageBase64 = await toBase64(event.currentTarget.files[0]);
      const imageContent = imageBase64.split(',')[1];
      const fileResult = await postFile({
        type: 'jpeg',
        fileName: srcName,
        fileContent: imageContent,
      }).unwrap();
      if (content.logo.id && fileResult && !id) {
        deleteFile(content.logo.id);
      }
      dispatch(
        updateContentHeaderById({
          content: {
            logo: {
              id: fileResult.data?.id,
            },
          },
        })
      );
    }
    event.target.value = '';
  };
  const handleDeleteBackgroundImage = async () => {
    if (!id) {
      dispatch(
        updateContentHeaderById({
          content: {
            backgroundImage: {
              id: null,
              name: '',
            },
          },
        })
      );
      await deleteFile(Number(content.backgroundImage.id));
    } else {
      dispatch(
        updateContentHeaderById({
          content: {
            backgroundImage: {
              id: null,
              name: '',
            },
          },
        })
      );
    }
  };

  const handleDeleteLogo = async () => {
    if (!id) {
      dispatch(
        updateContentHeaderById({
          content: {
            logo: {
              id: null,
              name: '',
            },
          },
        })
      );
      await deleteFile(Number(content.logo.id));
    } else {
      dispatch(
        updateContentHeaderById({
          content: {
            logo: {
              id: null,
              name: '',
            },
          },
        })
      );
    }
  };

  const handleChangeHasBackgroundImage = () => {
    dispatch(
      updateContentHeaderById({
        content: {
          hasBackgroundImage: !content.hasBackgroundImage,
        },
      })
    );
  };

  const handleChangeOpacity = (_event: Event | SyntheticEvent<Element, Event>, val: number | number[]) => {
    if (typeof val === 'number') {
      dispatch(
        updateContentHeaderById({
          style: {
            ['backgroundImageOpacity']: val,
          },
        })
      );
    }
  };

  const handleChangeBackgroundCover = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateContentHeaderById({
        style: {
          backgroundCover: (event.target as HTMLInputElement).value,
        },
      })
    );
  };

  const handleChangeBackgroundPosition = (tag: Tag) => {
    dispatch(
      updateContentHeaderById({
        style: {
          backgroundPosition: tag.value,
        },
      })
    );
  };

  return (
    <GridContainer container>
      <Grid item xs={12}>
        <Title> Header Side bar</Title>
      </Grid>
      <GridSubcontainer container alignItems='center'>
        <Grid item xs={2}>
          <Switch
            checked={content.hasBackgroundColor}
            onChange={handleBackgroundControl}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        </Grid>
        <Grid item xs={10}>
          <Subtitle>Background</Subtitle>
        </Grid>
        {content.hasBackgroundColor && (
          <Grid item xs={12}>
            <input type='color' value={style.backgroundColor} onChange={handleChangeColor} />
          </Grid>
        )}
      </GridSubcontainer>
      <GridSubcontainer container alignItems='center'>
        <Grid item xs={12}>
          <Divider variant='middle' />
        </Grid>
        <Grid item xs={2}>
          <Switch
            checked={content.hasBackgroundImage}
            onChange={handleChangeHasBackgroundImage}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        </Grid>
        <Grid item xs={10}>
          <Subtitle> Background Image</Subtitle>
        </Grid>
        {content.hasBackgroundImage && (
          <Grid container>
            {content.backgroundImage.id === null || isLoadingBackgroundImage ? (
              <Grid item xs={12}>
                <HiddenButton onClick={handleClickBackgroundImage}>
                  <Button sx={{ width: '100%' }} variant='secondary'>
                    Upload Image
                    <FileUploadIcon />
                  </Button>
                </HiddenButton>
              </Grid>
            ) : (
              <Fragment>
                <Grid item xs={10}>
                  <img alt='uploaded' src={backgroundImageData?.data.url} width='100%' />
                </Grid>
                <Grid item xs={2}>
                  <Grid container>
                    <Grid item>
                      <IconButton disableTouchRipple onClick={handleClickBackgroundImage}>
                        <ModeEditIcon sx={{ color: docGeniusPalette.primaryBlue }} />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton disableTouchRipple onClick={handleDeleteBackgroundImage}>
                        <DeleteIcon sx={{ color: docGeniusPalette.primaryBlue }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography>Opacity</Typography>
                  <Slider
                    size='small'
                    min={0}
                    value={style.backgroundImageOpacity}
                    step={0.1}
                    max={1}
                    aria-label='Small'
                    valueLabelDisplay='off'
                    onChange={handleChangeOpacity}
                    sx={{ '.MuiSlider-thumb': { boxShadow: 'none' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl>
                    <RadioGroup
                      value={style.backgroundCover}
                      name='background-position-group'
                      onChange={handleChangeBackgroundCover}
                    >
                      <FormControlLabel value='auto' control={<Radio />} label='Original Size' />
                      <FormControlLabel value='contain' control={<Radio />} label='Fit to Size' />
                      <FormControlLabel value='cover' control={<Radio />} label='Fill Container' />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography>Alignment</Typography>
                  <AlignmentRadioGroup
                    selected={{ label: style.backgroundPosition, value: style.backgroundPosition } as Tag}
                    handleChange={handleChangeBackgroundPosition}
                  />
                </Grid>
              </Fragment>
            )}
            <input type='file' onChange={handleUploadBackgroundImage} ref={hiddenBackgroundImageInput} hidden />
          </Grid>
        )}
      </GridSubcontainer>
      <GridSubcontainer container alignItems='center' gap={2}>
        <Grid item xs={12}>
          <Divider variant='middle' />
        </Grid>
        <Grid item xs={2}>
          <Switch
            checked={content.hasLogo}
            onChange={handleChangeHasLogo}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        </Grid>
        <Grid item xs={6}>
          <Subtitle> Logo</Subtitle>
        </Grid>
        {content.hasLogo && (
          <Grid container>
            {content.logo.id === null || isLoadingLogo ? (
              <Grid item xs={12}>
                <HiddenButton onClick={handleClickLogo}>
                  <Button sx={{ width: '100%' }} variant='secondary'>
                    Upload Image
                    <FileUploadIcon />
                  </Button>
                </HiddenButton>
              </Grid>
            ) : (
              <Fragment>
                <Grid item xs={10}>
                  <img alt='uploaded' src={logoData?.data.url} width='100%' />
                </Grid>
                <Grid item xs={2}>
                  <Grid container>
                    <Grid item>
                      <IconButton disableTouchRipple onClick={handleClickLogo}>
                        <ModeEditIcon sx={{ color: docGeniusPalette.primaryBlue }} />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton disableTouchRipple onClick={handleDeleteLogo}>
                        <DeleteIcon sx={{ color: docGeniusPalette.primaryBlue }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Fragment>
            )}
            <input type='file' onChange={handleUploadLogo} ref={hiddenLogoImageInput} hidden />
          </Grid>
        )}
      </GridSubcontainer>
      <GridSubcontainer container alignItems='center'>
        <Grid item xs={12}>
          <Divider variant='middle' />
        </Grid>
        <Grid item xs={2}>
          <Switch
            checked={content.hasHeaderDetails}
            onChange={handleChangeDetails}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        </Grid>
        <Grid item xs={10}>
          <Subtitle>Header Details</Subtitle>
        </Grid>
        {content.hasHeaderDetails && (
          <Grid container>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder='Change Label'
                value={content.text}
                onChange={_ =>
                  dispatch(
                    updateContentHeaderById({
                      content: {
                        text: _.target.value,
                      },
                    })
                  )
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography color={colorPalette.darkGray1}>Font Color</Typography>
              <input type='color' value={style.fontColor} onChange={handleChangePageNumberColor} />
            </Grid>
            <Grid item xs={12} marginTop='10px'>
              <Typography color={colorPalette.darkGray1}>Label Font</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <AzulDropdownMenu
                    options={styleOptions.labelFontSize}
                    value={style.labelFontSize}
                    onChange={handleUpdateByStyle('labelFontSize')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AzulDropdownMenu
                    options={styleOptions.labelFont}
                    value={style.labelFont}
                    onChange={handleUpdateByStyle('labelFont')}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </GridSubcontainer>
    </GridContainer>
  );
}
